<template>
  <div v-if="cmsError && !cmsLoading" class="flex items-center justify-center min-h-screen">
    <div class="text-center px-4">
      <p class="text-red-500 mb-2">{{ $t('location.loadError') }}</p>
      <p class="text-gray-500 text-sm">{{ cmsError }}</p>
    </div>
  </div>
  <div v-else-if="isEmptyProperty" class="container mx-auto px-4 py-8">
    <div class="max-w-4xl mx-auto">
      <div class="rounded-lg bg-gray-50 p-8 text-center text-gray-600">
        {{ $t('generic.emptyPage') }}
      </div>
    </div>
  </div>
  <div v-else-if="propertyData?.property" class="py-8 lg:py-10">
    <div class="container mx-auto px-4 max-w-7xl">
      <!-- Admin dashboard–style page title: left-aligned, gray-800 -->
      <div class="text-left mb-8">
        <h1 class="text-3xl font-bold text-gray-800">{{ $t('location.title') }}</h1>
        <p class="mt-2 text-base text-gray-700">{{ propertyData.location.address }}</p>
        <p class="mt-2 text-sm text-gray-500 max-w-3xl leading-relaxed">
          {{ $t('location.addressHint') }}
        </p>
      </div>

      <!-- Compact filter toolbar: chip-only, low height; groups separated on sm+ -->
      <div
        v-if="showFiltersToolbar"
        class="mb-4 flex flex-col gap-2 border-b border-gray-200/90 pb-3 sm:mb-5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-y-2"
        role="toolbar"
        :aria-label="$t('location.filtersToolbar')"
      >

        <div
          v-if="typeOptions.length"
          class="flex min-w-0 flex-wrap items-center gap-1.5"
          role="group"
          :aria-label="$t('location.filterType')"
        >
          <button type="button" :class="filterChip(!selectedTypeId)" @click="selectedTypeId = ''">
            {{ $t('location.filterAll') }}
          </button>
          <button
            v-for="opt in typeOptions"
            :key="opt.id"
            type="button"
            :class="filterChip(selectedTypeId === opt.id)"
            @click="selectedTypeId = opt.id"
          >
            <span class="mr-1 inline-block text-base leading-none" aria-hidden="true">{{ poiTypeIcon(opt.id) }}</span>
            {{ opt.label }}
          </button>
        </div>

        <div
          v-if="categoryOptions.length"
          class="flex min-w-0 flex-wrap items-center gap-1.5"
          :class="typeOptions.length ? 'sm:border-l sm:border-gray-200 sm:pl-4' : ''"
          role="group"
          :aria-label="$t('location.filterCategories')"
        >
          <button
            type="button"
            :class="filterChip(selectedCategoryIds.length === 0)"
            @click="selectedCategoryIds = []"
          >
            {{ $t('location.filterAll') }}
          </button>
          <button
            v-for="opt in categoryOptions"
            :key="opt.id"
            type="button"
            :class="filterChip(selectedCategoryIds.includes(opt.id))"
            @click="toggleCategory(opt.id)"
          >
            {{ opt.label }}
          </button>
        </div>

        <div
          v-if="poiItems.length"
          class="flex min-w-0 flex-wrap items-center gap-1.5"
          :class="
            categoryOptions.length || typeOptions.length ? 'sm:border-l sm:border-gray-200 sm:pl-4' : ''
          "
          role="group"
          :aria-label="$t('location.filterDistance')"
        >
          <button type="button" :class="filterChip(isNoMaxDistance)" @click="clearMaxDistance">
            {{ $t('location.filterAll') }}
          </button>
          <button
            v-for="opt in distancePresetOptions"
            :key="opt.meters"
            type="button"
            :class="filterChip(selectedMaxDistanceMeters === opt.meters)"
            @click="selectPresetDistance(opt.meters)"
          >
            {{ opt.label }}
          </button>
          <label
            class="inline-flex items-stretch overflow-hidden rounded-md border bg-white transition focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-0"
            :class="
              isCustomMaxDistance
                ? 'border-blue-600 shadow-sm ring-2 ring-blue-500/25'
                : 'border-gray-200 hover:border-gray-300'
            "
          >
            <span class="sr-only">{{ $t('location.distanceCustomHint') }}</span>
            <input
              v-model="customMetersDraft"
              type="number"
              inputmode="numeric"
              min="1"
              max="999999"
              :placeholder="$t('location.distanceMaxPlaceholder')"
              class="w-[4.25rem] min-w-0 border-0 bg-transparent py-1 pl-2 pr-0 text-[11px] text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-0"
              @keydown.enter.prevent="applyCustomDistanceFromInput"
              @change="applyCustomDistanceFromInput"
              @blur="applyCustomDistanceFromInput"
            />
            <span class="pointer-events-none self-center pr-2 text-[10px] font-medium text-gray-500">m</span>
          </label>
        </div>
      </div>

      <!-- Map | list: lg flex row + min-height so both panels match vertically; hints aligned below -->
      <div class="flex flex-col gap-3 lg:gap-4">
        <div
          class="loc2-panels flex flex-col lg:flex-row gap-4 lg:gap-5 lg:items-stretch"
        >
          <div class="min-w-0 flex-1 flex flex-col min-h-0 loc2-map-col">
            <div
              class="loc2-map-shell flex flex-col rounded-lg border border-gray-200 bg-white shadow-md overflow-hidden"
            >
              <LocationMap
                class="loc2-map h-full min-h-0 w-full flex-1"
                :coordinates="propertyData.location.coordinates"
                :amenities="filteredPois"
                :selected-id="selectedId"
                :route-geometry="routeGeometry"
                :max-distance-meters="mapFrameRadiusMeters"
                @select="selectedId = $event"
              />
            </div>
            <p class="mt-2 text-xs text-gray-500 leading-relaxed lg:hidden">
              {{ $t('location.routeHint') }}
            </p>
          </div>

          <div
            class="w-full lg:w-[400px] xl:w-[440px] shrink-0 flex flex-col min-h-0 loc2-list-col"
          >
            <div
              class="loc2-list-scroll flex flex-col rounded-lg border border-gray-200 bg-white shadow-md overflow-y-auto overflow-x-hidden"
            >
              <div
                v-for="poi in filteredPois"
                :key="poi.id"
                :ref="(el) => setPoiCardRef(poi.id, el)"
                type="button"
                class="loc2-card flex gap-3 p-3 sm:p-3.5 border-b border-gray-100 last:border-b-0 text-left cursor-pointer transition-colors hover:bg-gray-50/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
                :class="selectedId === poi.id ? 'bg-blue-50/90 ring-2 ring-inset ring-blue-500' : ''"
                role="button"
                tabindex="0"
                @click="selectedId = poi.id"
                @keydown.enter.prevent="selectedId = poi.id"
              >
                <div class="w-32 shrink-0 aspect-video rounded-lg overflow-hidden bg-gray-200 border border-gray-100/80">
                  <img
                    v-if="poi.image"
                    :src="publicAssetUrl(poi.image)"
                    :alt="poi.name"
                    class="w-full h-full object-cover"
                  />
                </div>
                <div class="min-w-0 flex-1 py-0.5">
                  <div class="flex items-start justify-between gap-2">
                    <h3 class="text-sm font-semibold text-gray-800 leading-snug line-clamp-2">{{ poi.name }}</h3>
                    <span class="shrink-0 text-[11px] font-medium text-blue-700 whitespace-nowrap">
                      {{ poi.distanceLabel }}
                    </span>
                  </div>
                  <p class="text-[11px] text-blue-600 font-medium mt-0.5 truncate">{{ poi.typeLabel }}</p>
                  <p v-if="poiCategoryLine(poi)" class="text-[11px] text-gray-500 mt-0.5 truncate">
                    {{ poiCategoryLine(poi) }}
                  </p>
                  <p class="text-[11px] text-gray-600 mt-1 line-clamp-2 leading-snug">{{ poi.description }}</p>
                </div>
              </div>
              <div v-if="filteredPois.length === 0" class="p-8 text-center text-sm text-gray-500">
                {{ $t('location.emptyList') }}
              </div>
            </div>
            <p class="mt-2 text-xs text-gray-500 leading-relaxed lg:hidden">
              {{ $t('location.feedbackHint') }}
              <router-link
                :to="localeFeedbackPath"
                class="text-blue-600 font-medium hover:text-blue-700 underline-offset-2 hover:underline rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
                >{{ $t('location.feedbackLink') }}</router-link
              >.
            </p>
          </div>
        </div>

        <!-- Desktop / lg+: hints aligned under map column and list column -->
        <div
          class="hidden lg:grid grid-cols-[1fr_400px] xl:grid-cols-[1fr_440px] gap-2 lg:gap-5 text-left"
        >
          <p class="text-xs text-gray-500 leading-relaxed">
            {{ $t('location.routeHint') }}
          </p>
          <p class="text-xs text-gray-500 leading-relaxed">
            {{ $t('location.feedbackHint') }}
            <router-link
              :to="localeFeedbackPath"
              class="text-blue-600 font-medium hover:text-blue-700 underline-offset-2 hover:underline rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
              >{{ $t('location.feedbackLink') }}</router-link
            >.
          </p>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="flex items-center justify-center min-h-screen">
    <p class="text-gray-500">{{ $t('location.loading') }}</p>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import LocationMap from '../components/location/LocationMap.vue';
import { useLocalePropertyContent } from '../composables/useLocalePropertyContent';
import { usePointsOfInterest } from '../composables/usePointsOfInterest';
import { publicAssetUrl } from '../utils/publicUrl';
import { poiTypeIcon } from '../constants/poiTypeIcons.js';
import { CONFIG } from '../config.js';

const route = useRoute();
const { t } = useI18n();

const { content, isLoading: cmsLoading, isEmptyProperty, errorMessage: cmsError } = useLocalePropertyContent();
const { items: poiItems } = usePointsOfInterest();

const propertyData = computed(() => content.value);

const selectedId = ref('');
/** @type {Map<string, HTMLElement | null>} */
const poiCardEls = new Map();

function setPoiCardRef(id, el) {
  if (el) poiCardEls.set(id, el);
  else poiCardEls.delete(id);
}

function scrollSelectedPoiIntoView(id) {
  if (!id) return;
  nextTick(() => {
    const el = poiCardEls.get(id);
    if (!el || typeof el.scrollIntoView !== 'function') return;
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth', inline: 'nearest' });
    if (typeof el.focus === 'function') {
      try {
        el.focus({ preventScroll: true });
      } catch (_) {
        el.focus();
      }
    }
  });
}
const routeGeometry = ref(null);
const selectedTypeId = ref('');
const selectedCategoryIds = ref([]);

const DISTANCE_PRESETS_M = [500, 2000, 5000];
const DEFAULT_MAX_DISTANCE_M = DISTANCE_PRESETS_M[0];

/** Max straight-line distance from property (m); null = no limit. Default matches first preset (map + list). */
const selectedMaxDistanceMeters = ref(DEFAULT_MAX_DISTANCE_M);
/** Draft value for custom max distance input (m); applied on blur / Enter */
const customMetersDraft = ref('');

const isNoMaxDistance = computed(() => selectedMaxDistanceMeters.value == null);

const isCustomMaxDistance = computed(() => {
  const m = selectedMaxDistanceMeters.value;
  return m != null && Number.isFinite(m) && !DISTANCE_PRESETS_M.includes(m);
});

/**
 * Always a number for LocationMap so prop updates reliably (0 = show whole area / no radius framing).
 */
const mapFrameRadiusMeters = computed(() => {
  const m = selectedMaxDistanceMeters.value;
  return m != null && Number.isFinite(m) && m > 0 ? m : 0;
});

const distancePresetOptions = computed(() =>
  DISTANCE_PRESETS_M.map((meters) => ({
    meters,
    label:
      meters >= 1000
        ? t('location.distanceKm', { n: meters / 1000 })
        : t('location.distanceMeters', { n: meters })
  }))
);

const localeFeedbackPath = computed(() => {
  const lang = typeof route.params?.lang === 'string' ? route.params.lang : CONFIG.DEFAULT_LOCALE;
  const qs = new URLSearchParams();
  qs.set('source', 'location');
  if (selectedPoi.value?.id) qs.set('poiId', String(selectedPoi.value.id));
  if (selectedPoi.value?.name) qs.set('poiName', String(selectedPoi.value.name));
  const query = qs.toString();
  return `/${lang}/feedback${query ? `?${query}` : ''}`;
});

function filterChip(active) {
  return [
    'rounded-md border px-2.5 py-1 text-[11px] font-medium transition',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
    active
      ? 'border-blue-700 bg-blue-600 text-white shadow-sm'
      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
  ];
}

function selectPresetDistance(meters) {
  selectedMaxDistanceMeters.value = meters;
}

function clearMaxDistance() {
  selectedMaxDistanceMeters.value = null;
}

function applyCustomDistanceFromInput() {
  const raw = String(customMetersDraft.value ?? '').trim();
  if (!raw) {
    if (isCustomMaxDistance.value) {
      selectedMaxDistanceMeters.value = null;
    }
    return;
  }
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n) || n < 1) {
    const cur = selectedMaxDistanceMeters.value;
    customMetersDraft.value =
      cur != null && !DISTANCE_PRESETS_M.includes(cur) ? String(cur) : '';
    return;
  }
  const clamped = Math.min(999_999, Math.max(1, n));
  selectedMaxDistanceMeters.value = clamped;
  customMetersDraft.value = String(clamped);
}

watch(selectedMaxDistanceMeters, (m) => {
  if (m == null) {
    customMetersDraft.value = '';
    return;
  }
  if (DISTANCE_PRESETS_M.includes(m)) {
    customMetersDraft.value = '';
  } else {
    customMetersDraft.value = String(m);
  }
});

function toggleCategory(id) {
  const set = new Set(selectedCategoryIds.value);
  if (set.has(id)) set.delete(id);
  else set.add(id);
  selectedCategoryIds.value = [...set];
}

const typeOptions = computed(() => {
  const map = new Map();
  for (const a of poiItems.value) {
    const id = a.typeId != null ? String(a.typeId).trim() : '';
    const label = (a.typeLabel != null ? String(a.typeLabel) : id).trim();
    if (!id || !label) continue;
    if (!map.has(id)) map.set(id, label);
  }
  return [...map.entries()].map(([id, label]) => ({ id, label }));
});

const categoryOptions = computed(() => {
  const map = new Map();
  for (const a of poiItems.value) {
    const ids = Array.isArray(a.categoryIds) ? a.categoryIds : [];
    const labels = Array.isArray(a.categoryLabels) ? a.categoryLabels : [];
    ids.forEach((id, i) => {
      const cid = id != null ? String(id).trim() : '';
      const lab = (labels[i] != null ? String(labels[i]) : cid).trim();
      if (!cid || !lab) return;
      if (!map.has(cid)) map.set(cid, lab);
    });
  }
  return [...map.entries()].map(([id, label]) => ({ id, label }));
});

const showFiltersToolbar = computed(
  () => categoryOptions.value.length > 0 || typeOptions.value.length > 0 || poiItems.value.length > 0
);

const filteredPois = computed(() => {
  let rows = poiItems.value;
  if (selectedTypeId.value) {
    rows = rows.filter((a) => a.typeId === selectedTypeId.value);
  }
  if (selectedCategoryIds.value.length > 0) {
    rows = rows.filter((a) =>
      (a.categoryIds || []).some((cid) => selectedCategoryIds.value.includes(cid))
    );
  }
  const maxM = selectedMaxDistanceMeters.value;
  if (maxM != null && Number.isFinite(maxM)) {
    rows = rows.filter((a) => {
      const d = a.distance;
      if (typeof d !== 'number' || Number.isNaN(d)) return false;
      return d <= maxM;
    });
  }
  return rows;
});

watch(filteredPois, (rows) => {
  if (selectedId.value && !rows.some((r) => r.id === selectedId.value)) {
    selectedId.value = '';
  }
});

watch(selectedId, (id) => {
  scrollSelectedPoiIntoView(id);
});

function poiCategoryLine(poi) {
  const labels = Array.isArray(poi.categoryLabels) ? poi.categoryLabels.filter(Boolean) : [];
  return labels.length ? labels.join(' · ') : '';
}

async function fetchWalkingRoute(from, to) {
  routeGeometry.value = null;
  if (!from || !to || typeof from.lat !== 'number' || typeof to.lat !== 'number') return;
  try {
    const url = `https://router.project-osrm.org/route/v1/foot/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson`;
    const res = await fetch(url);
    const data = await res.json();
    if (data.code !== 'Ok' || !data.routes?.[0]?.geometry) return;
    routeGeometry.value = data.routes[0].geometry;
  } catch (_) {
    routeGeometry.value = null;
  }
}

const selectedPoi = computed(() => poiItems.value.find((p) => p.id === selectedId.value) || null);

watch(
  [selectedPoi, propertyData],
  () => {
    const prop = propertyData.value?.location?.coordinates;
    const poi = selectedPoi.value?.coordinates;
    if (prop && poi) fetchWalkingRoute(prop, poi);
    else routeGeometry.value = null;
  },
  { immediate: true }
);
</script>

<style scoped>
/* Shared fixed height: map and list match; modest on large / Full HD (not 60vh). */
.loc2-panels {
  --loc2-panel-h: min(260px, 42vh);
}
@media (min-width: 1024px) {
  .loc2-panels {
    --loc2-panel-h: min(380px, 36vh);
  }
}
.loc2-map-col,
.loc2-list-col {
  min-height: 0;
}
.loc2-map-shell {
  height: var(--loc2-panel-h);
  min-height: var(--loc2-panel-h);
  max-height: var(--loc2-panel-h);
  position: relative;
  z-index: 0;
  isolation: isolate;
}
.loc2-list-scroll {
  height: var(--loc2-panel-h);
  min-height: var(--loc2-panel-h);
  max-height: var(--loc2-panel-h);
}
/* Flatten LocationMap’s own border/shadow so the shell provides chrome. */
.loc2-map-shell :deep(> div) {
  height: 100% !important;
  min-height: 100% !important;
  max-height: 100% !important;
  border-radius: 0;
  border-width: 0;
  box-shadow: none;
}
.loc2-map :deep(> div) {
  height: 100% !important;
  min-height: 100% !important;
}
</style>
